import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { JibexAccountView, JibexCheckLine, JibexSaveBody, JibexTestResponse } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * Lot L5a — `GET/PUT /api/v1/merchant/delivery/jibex`, `POST …/test`, `GET …/checks`. All four are
 * `merchant_owner`-only on the server; the `enabled` flag only spares a Staff session a request that is certain to be
 * refused (the server remains the authority).
 *
 * The secret travels in ONE direction only: it is part of the body of `useSaveJibexAccountMutation` and of nothing
 * that comes back. It is never put in the query cache, never in a query key, never in browser storage.
 */

const BASE = '/merchant/delivery/jibex';

const accountKey = (shopId: string | null): readonly unknown[] => ['jibex-account', shopId];
const checksKey = (shopId: string | null): readonly unknown[] => ['jibex-checks', shopId];

export function useJibexAccountQuery(enabled: boolean): UseQueryResult<JibexAccountView, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: accountKey(activeShopId),
    queryFn: () => apiRequest<JibexAccountView>(BASE, { accessToken, shopId: activeShopId }),
    enabled: enabled && accessToken !== null && activeShopId !== null,
    // A verdict the owner has just produced must not be replaced by a cached older one.
    staleTime: 0,
  });
}

export function useJibexChecksQuery(
  enabled: boolean,
): UseQueryResult<readonly JibexCheckLine[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: checksKey(activeShopId),
    queryFn: async () => {
      const response = await apiRequest<{ readonly checks: readonly JibexCheckLine[] }>(
        `${BASE}/checks`,
        { accessToken, shopId: activeShopId },
      );
      return response.checks;
    },
    enabled: enabled && accessToken !== null && activeShopId !== null,
    staleTime: 0,
  });
}

export function useSaveJibexAccountMutation(): UseMutationResult<
  JibexAccountView,
  ApiError,
  JibexSaveBody
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    // The variables of this mutation CARRY THE SECRET. TanStack keeps a mutation's variables in memory (and shows them
    // in its devtools) until the mutation is garbage-collected — so it is collected at once (`gcTime: 0`), and the page
    // also calls `reset()` as soon as the request settles.
    gcTime: 0,
    mutationFn: (body: JibexSaveBody) =>
      apiRequest<JibexAccountView>(BASE, {
        method: 'PUT',
        body,
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: async (view) => {
      queryClient.setQueryData(accountKey(activeShopId), view);
      await queryClient.invalidateQueries({ queryKey: checksKey(activeShopId) });
    },
  });
}

export function useTestJibexMutation(): UseMutationResult<JibexTestResponse, ApiError, void> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<JibexTestResponse>(`${BASE}/test`, {
        method: 'POST',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: async (result) => {
      queryClient.setQueryData(accountKey(activeShopId), result.account);
      await queryClient.invalidateQueries({ queryKey: checksKey(activeShopId) });
    },
  });
}
